/**
 * Previous Exports — per-project
 * --------------------------------
 * Lists recent CSV exports for a single project. Lets an admin re-download
 * completed exports (presigned URL, 1 h) and cancel in-flight ones.
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  RefreshCw,
  XCircle,
  FileSpreadsheet,
  AlertTriangle,
} from 'lucide-react';

import { AppHeader } from '@/components/AppHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import LoadingSpinner from '@/components/LoadingSpinner';
import { useProject } from '@/hooks/useProjects';
import { useReportExports } from '@/hooks/useReportExports';
import { reportsApi, type ExportJobSummary, type ExportJobStatus } from '@/services/reports';
import { toast } from '@/lib/toast';

const DATE_FMT: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, DATE_FMT);
}

function statusBadgeClass(status: ExportJobStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-success/10 text-success border-success/20';
    case 'processing':
      return 'bg-info/10 text-info border-info/20';
    case 'failed':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'cancelled':
      return 'bg-muted text-muted-foreground border-muted';
    default:
      return 'bg-muted text-muted-foreground border-muted';
  }
}

function FilterPills({ filters }: { filters?: ExportJobSummary['filters'] }) {
  const pills = useMemo(() => {
    if (!filters) return [] as { label: string; values: string[] }[];
    return (['domain', 'area', 'status', 'tester'] as const)
      .map((key) => ({ label: key, values: filters[key] ?? [] }))
      .filter((p) => p.values.length > 0);
  }, [filters]);

  if (pills.length === 0) {
    return <span className="text-xs text-muted-foreground italic">No filters</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {pills.map((p) => (
        <Badge
          key={p.label}
          variant="outline"
          className="text-xs font-normal"
          title={p.values.join(', ')}
        >
          {p.label}: {p.values.length}
        </Badge>
      ))}
    </div>
  );
}

function ProgressCell({ item }: { item: ExportJobSummary }) {
  if (item.status !== 'processing') {
    return (
      <span className="text-xs text-muted-foreground">
        {item.scenarios_processed}/{item.total_scenarios}
      </span>
    );
  }
  const total = item.total_scenarios || 0;
  const done = item.scenarios_processed || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex flex-col gap-1 min-w-[80px]">
      <span className="text-xs tabular-nums">{pct}%</span>
      <div className="h-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-info transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function ProjectExports() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: project, isLoading: projectLoading } = useProject(id || '');
  const {
    exports,
    count,
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useReportExports(
    id ? { kind: 'project', projectId: id } : { kind: 'global' },
  );

  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const handleCancel = useCallback(
    async (jobId: string) => {
      setCancellingId(jobId);
      try {
        const result = await reportsApi.cancelCsvExport(jobId);
        if (result.status === 'cancelled') {
          toast.success('Export cancelled');
        } else {
          toast.info(
            result.message || `Job is already ${result.status}.`,
          );
        }
        await refetch();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to cancel export',
        );
      } finally {
        setCancellingId(null);
      }
    },
    [refetch],
  );

  const handleCopyLink = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Download link copied (valid for 1 hour)');
    } catch {
      toast.error('Unable to copy link');
    }
  }, []);

  if (projectLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader title="Previous Exports" showBack onBack={() => navigate(-1)} />
        <main className="max-w-6xl mx-auto p-6">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  const projectLabel = project
    ? `${project.project_number} · ${project.name}`
    : 'Project';

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Previous Exports" showBack onBack={() => navigate(-1)} />
      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3 text-sm">
          <Button asChild variant="ghost" size="sm" className="h-8 gap-1.5">
            <Link to={`/admin/projects/${id}/report`}>
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to report
            </Link>
          </Button>
          <span className="text-muted-foreground">·</span>
          <span className="font-medium">{projectLabel}</span>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                Recent CSV Exports
              </CardTitle>
              <CardDescription>
                Exports are retained for 24 hours. Completed jobs can be
                re-downloaded via a signed link valid for 1 hour.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => refetch()}
              disabled={isRefetching}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isRefetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          </CardHeader>

          <CardContent>
            {error ? (
              <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4 mt-0.5" />
                <span>
                  Failed to load exports.{' '}
                  {error instanceof Error ? error.message : 'Please retry.'}
                </span>
              </div>
            ) : isLoading ? (
              <LoadingSpinner />
            ) : exports.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No exports yet. Use the Export button on the report page to
                generate a CSV.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started</TableHead>
                    <TableHead>Filters</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {exports.map((item) => (
                    <TableRow key={item.job_id}>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col">
                          <span>{formatDate(item.created_at)}</span>
                          <span className="text-xs text-muted-foreground truncate max-w-[180px]">
                            {item.requested_by}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <FilterPills filters={item.filters} />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={statusBadgeClass(item.status)}
                        >
                          {item.status}
                        </Badge>
                        {item.error_message && (
                          <div
                            className="mt-1 text-xs text-muted-foreground line-clamp-2 max-w-[240px]"
                            title={item.error_message}
                          >
                            {item.error_message}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <ProgressCell item={item} />
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {item.status === 'completed' && item.download_url && (
                          <>
                            <Button
                              asChild
                              size="sm"
                              variant="outline"
                              className="h-8 gap-1"
                            >
                              <a
                                href={item.download_url}
                                download={`report-${item.job_id}.csv`}
                              >
                                <Download className="h-3.5 w-3.5" />
                                Download
                              </a>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8"
                              onClick={() => handleCopyLink(item.download_url!)}
                            >
                              Copy link
                            </Button>
                          </>
                        )}
                        {item.status === 'processing' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1 text-destructive"
                            disabled={cancellingId === item.job_id}
                            onClick={() => handleCancel(item.job_id)}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            {cancellingId === item.job_id ? 'Cancelling…' : 'Cancel'}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {count > 0 && (
              <div className="mt-3 text-xs text-muted-foreground">
                Showing {count} export{count === 1 ? '' : 's'}.
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
